import React from "react"
import { type ApolloError } from "@apollo/client"
import {
  type Transaction,
  useUnmatchTransactionsMutation,
} from "@digits-graphql/frontend/graphql-bearer"
import { SvgAlertTriangleSolid } from "@digits-shared/components/SVGIcons/solid/AlertTriangleSolid.svg"
import { SvgCheckCircleSolid } from "@digits-shared/components/SVGIcons/solid/CheckCircleSolid.svg"
import useSession from "@digits-shared/hooks/useSession"
import { v4 as generateUUID } from "uuid"
import { type FrontendTransaction } from "src/frontend/components/OS/Applications/Accounting/TransactionDetail/state/types"
import { useTransactionsCanBeUnmatched } from "src/frontend/components/OS/Applications/Shared/Accounting/Matching/hooks/useTransactionsCanBeUnmatched"
import { useChromeAlertContext } from "src/frontend/components/Shared/Alerts/ChromeAlertsContext"
import type FrontendSession from "src/frontend/session"

/*
  INTERFACES
*/

interface Props {
  transactions: Transaction[] | FrontendTransaction
  onUnmatch?: (transactions: Transaction[] | FrontendTransaction) => void
  onError?: (error: ApolloError) => void
}

/*
  HOOKS
*/

// Validates transactions can be unmatched and calls the mutation
// to unmatch them. If successful, it will call the onUnmatch callback
export const useUnmatchTransactions = ({ transactions, onUnmatch, onError }: Props) => {
  const { currentLegalEntityId: legalEntityId } = useSession<FrontendSession>()
  const ledgerTransactionIds = useLedgerTransactionIds(transactions)
  const [unmatchTransactions] = useUnmatchTransactionsMutation({
    variables: {
      legalEntityId,
      ledgerTransactionIds,
    },
  })
  const { valid, reason } = useTransactionsCanBeUnmatched(transactions)
  const { addAlert } = useChromeAlertContext()

  return React.useCallback(() => {
    if (!valid)
      return addAlert({
        id: generateUUID(),
        message: "Cannot unmatch transaction",
        description: reason,
        autoDismiss: true,
        SVGComponent: SvgAlertTriangleSolid,
      })

    return unmatchTransactions()
      .then(() => {
        addAlert({
          id: generateUUID(),
          message: "Transactions unmatched",
          description: "Updates may take a few minutes to reflect",
          autoDismiss: true,
          SVGComponent: SvgCheckCircleSolid,
        })
        onUnmatch?.(transactions)
      })
      .catch((err) => {
        addAlert({
          id: generateUUID(),
          message: "Error unmatching transactions",
          autoDismiss: true,
          SVGComponent: SvgAlertTriangleSolid,
        })
        onError?.(err)
      })
  }, [addAlert, onError, onUnmatch, reason, transactions, unmatchTransactions, valid])
}

const useLedgerTransactionIds = (transactions: Transaction[] | FrontendTransaction): string[] => {
  const isFrontendTransaction = useIsFrontendTransaction(transactions)
  return React.useMemo(() => {
    if (isFrontendTransaction) {
      return Array.from(
        new Set([transactions.ledgerTransactionId, ...transactions.matchedLedgerTransactionIds])
      )
    }

    // For unmatching the BE requires all ledgerTransactionIds involved in the match
    // to be passed in. This includes the ledgerTransactionId of the Transaction
    // and the ledgerTransactionIds of all matched transactions.
    // Theoretically, the first view transaction should have all of the ids in union of Transaction.LedgerTransactionId and Transaction.MatchedLedgerTransactionIds
    // but to be safe we are using a reduce to get all of the ids.
    return Array.from(
      transactions.reduce((ids, transaction) => {
        ids.add(transaction.ledgerTransactionId)
        transaction.matchedLedgerTransactionIds.forEach((id) => ids.add(id))
        return ids
      }, new Set<string>())
    )
  }, [transactions, isFrontendTransaction])
}

/*
  HELPERS
*/

function useIsFrontendTransaction(
  transactions: Transaction[] | FrontendTransaction
): transactions is FrontendTransaction {
  return React.useMemo(() => !Array.isArray(transactions), [transactions])
}
